import { Button } from '@monorepo/nova-ui';
import { Tag } from '@monorepo/nova-ui';
import { IconButton, Stack, Table, TableCell, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Modal content container
 * Uses theme spacing: maxWidth = 222 * 4px = 888px, minWidth = 200 * 4px = 800px
 */
export const StyledModalContent = styled(Stack)(({ theme }) => ({
  width: '100%',
  maxWidth: theme.spacing(222),
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    minWidth: theme.spacing(200),
  },
}));

/**
 * Header container with title and close button
 */
export const StyledHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
}));

/**
 * Close button in top right - purple colored per Figma
 */
export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: theme.palette.primary.main,
}));

/**
 * Title typography
 * Mobile: 28px, bold, two lines allowed
 */
export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"General Sans", sans-serif',
  fontWeight: 700,
  fontSize: theme.typography.pxToRem(28),
  lineHeight: 1,
  letterSpacing: 0,
  [theme.breakpoints.up('md')]: {
    ...theme.typography.h5,
  },
}));

/**
 * Custom table container
 */
export const StyledTableContainer = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(2),
  overflowX: 'auto',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(3),
  },
}));

/**
 * Custom table
 */
export const StyledTable = styled(Table)(({ theme }) => ({
  width: '100%',
  tableLayout: 'auto',
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.background.default,
  },
  '& .MuiTableCell-head': {
    fontWeight: theme.typography.fontWeightMedium || 500,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(11),
    padding: theme.spacing(1, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(12),
      padding: theme.spacing(2, 3),
    },
  },
  '& .MuiTableCell-body': {
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(1, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(14),
      padding: theme.spacing(2, 3),
    },
  },
  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
    borderBottom: 'none',
  },
  // Mobile: Hide separate Side, Action, Fee columns
  [theme.breakpoints.down('md')]: {
    '& .mobile-hide': {
      display: 'none',
    },
  },
  // Desktop: Hide combined columns
  [theme.breakpoints.up('md')]: {
    '& .desktop-hide': {
      display: 'none',
    },
  },
}));

/**
 * Table cell with right alignment for numbers
 */
export const StyledTableCellRight = styled(TableCell)(() => ({
  textAlign: 'right',
}));

/**
 * Combined Side/Action cell for mobile view
 */
export const StyledSideActionCell = styled(TableCell)(() => ({
  '&.MuiTableCell-body': {
    verticalAlign: 'top',
  },
}));

/**
 * Combined Price/Fee cell for mobile view
 */
export const StyledPriceFeeCell = styled(TableCell)(() => ({
  textAlign: 'right',
  '&.MuiTableCell-body': {
    verticalAlign: 'top',
  },
}));

/**
 * Content wrapper for stacked items inside a cell
 */
export const StyledCellContent = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

/**
 * Action text below side tag (mobile view)
 */
export const StyledActionText = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(10),
  color: theme.palette.text.secondary,
}));

/**
 * Fee text below price (mobile view)
 */
export const StyledFeeText = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(10),
  color: theme.palette.text.secondary,
}));

/**
 * Side tag styled component
 */
export const StyledSideTag = styled(Tag)(({ theme }) => ({
  padding: theme.spacing(0),
  fontSize: theme.typography.pxToRem(10),
}));

/**
 * Footer container with close button
 */
export const StyledFooter = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  marginTop: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(4),
  },
}));

/**
 * Close button at bottom - purple outlined per Figma
 * minWidth = 15 * 8px = 120px
 */
export const StyledCloseButtonBottom = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(15),
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}10`,
  },
}));

/**
 * Loading container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(25),
}));

/**
 * Trade ID text with smaller font for compact display
 * Wraps to two lines on mobile (maxWidth = 15 * 8px = 120px)
 */
export const StyledTradeId = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(8),
  display: 'block',
  wordBreak: 'break-all',
  maxWidth: theme.spacing(15),
  [theme.breakpoints.up('md')]: {
    fontSize: theme.typography.pxToRem(11),
    wordBreak: 'normal',
    maxWidth: 'none',
  },
}));
